class PlugSlider extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        const init = () => {
            this.sliderId = this.dataset.sliderId;

            this.elements = {
                swiper: document.getElementById(this.sliderId),
                swiperWrapper: this.querySelector(".swiper-wrapper"),
                slides: this.querySelectorAll(".swiper-slide"),
            };

            if (!this.sliderId || !this.elements.swiper) {
                console.error("PLUG: Swiper element cannot be found.");
                return this.hideSection();
            }

            this.initSlider();
        };

        if (window.Swiper) {
            init();
        } else {
            setTimeout(() => {
                if (!window.Swiper) {
                    console.error("PLUG: Swiper.js is not loaded. Please include Swiper.js to use the plug-slider component.");
                    return this.hideSection();
                } else {
                    init();
                }
            }, 250);
        }
    }

    initSlider() {
        const defaultSettings = {
            a11y: true,
            centerInsufficientSlides: true,
            grabCursor: true,
            slidesPerView: "auto",
        };

        const settingsScript = this.querySelector("script[data-slider-custom-settings]");
        const customSettings = (
            settingsScript
                ? JSON.parse(settingsScript.innerHTML)
                : {}
        );

        this.swiper = new Swiper(
            `#${this.sliderId}`,
            Object.assign(defaultSettings, customSettings)
        );
    }

    disconnectedCallback() {
        if (this.swiper && typeof this.swiper.destroy === "function") {
            this.swiper.destroy();
        }
    }

    hideSection() {
        const section = this.closest(".plug-section");
        if (section) {
            section.style.display = "none";
        }
    }
}
if (!customElements.get("plug-slider")) {
    customElements.define("plug-slider", PlugSlider);
}

if (!customElements.get("plug-accordion")) {
    class PlugAccordion extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.bodyHeights = [];
            this.items = Array.from(this.getElementsByClassName("plug-accordion__item"));
            this.headings = Array.from(this.getElementsByClassName("plug-accordion__heading"));
            this.bodies = Array.from(this.getElementsByClassName("plug-accordion__body"));
            this.measureBodyHeights();
            this.initListeners();
        }

        measureBodyHeights() {
            const clones = this.bodies.map(answer => {
                const clone = answer.cloneNode(true);
                clone.classList.add("is-measuring");
                clone.style.height = "";
                return answer.parentElement.appendChild(clone);
            });

            afterNextFrame(() => {
                // Measure the clones.
                this.bodyHeights = clones.map(clone => {
                    return clone.getBoundingClientRect().height;
                });
                // Update the current height of any open answers.
                this.bodies.forEach((answer, index) => {
                    if (this.items[index].dataset.open === "true") {
                        answer.style.height = `${this.bodyHeights[index]}px`;
                    }
                });
                // Delete the clones.
                clones.forEach(clone => {
                    clone.parentElement.removeChild(clone);
                });
            });
        }

        initListeners() {
            this.headings.forEach((heading, index) => {
                heading.addEventListener("click", (e) => {
                    if (this.isOpen(index)) {
                        this.close(index);
                    } else {
                        this.open(index);
                    }
                });
            });
            const resizeThrottler = throttlerConstructor(500);
            window.addEventListener("resize", () => {
                resizeThrottler(() => {
                    this.measureBodyHeights();
                });
            });
        }

        open(index) {
            this.items[index].dataset.open = "true";
            this.bodies[index].style.height = `${this.bodyHeights[index]}px`;
        }

        close(index) {
            this.bodies[index].style.height = "";
            this.items[index].dataset.open = "false";
        }

        isOpen(index) {
            return this.headings[index].closest(".plug-accordion__item").dataset.open === "true";
        }
    }

    customElements.define("plug-accordion", PlugAccordion);

    function afterNextFrame(cb) {
        window.requestAnimationFrame(() => {
            setTimeout(cb, 0);
        });
    }

    function throttlerConstructor(delayMs = 0) {
        let queuedCallback;
        return function onEvent(callback) {
            if (!queuedCallback) {
                queuedCallback = callback;
                if (delayMs === 0) {
                    // We are limiting calls to the callback to once per repaint.
                    window.requestAnimationFrame(function () {
                        if (queuedCallback) {
                            const cb = queuedCallback;
                            queuedCallback = null;
                            cb();
                        }
                    });
                } else {
                    // We are limiting the calls to the callback to some specific interval of time.
                    setTimeout(() => {
                        if (queuedCallback) {
                            const cb = queuedCallback;
                            queuedCallback = null;
                            cb();
                        }
                    }, delayMs);
                }
            } else {
                queuedCallback = callback;
            }
        };
    }
}

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
    const { mediaQueries } = theme;
    if (!mediaQueries) return;

    const mqKeys = Object.keys(mediaQueries);
    const mqLists = {};
    theme.mediaMatches = {};

    /**
     * Handles a media query (breakpoint) change.
     */
    const handleMqChange = () => {
        const newMatches = mqKeys.reduce((acc, media) => {
            acc[media] = !!(mqLists[media] && mqLists[media].matches);
            return acc;
        }, {});

        // Update mediaMatches values after breakpoint change.
        Object.keys(newMatches).forEach((key) => {
            theme.mediaMatches[key] = newMatches[key];
        });

        window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
    };

    mqKeys.forEach((mq) => {
        // Create mqList object for each media query.
        mqLists[mq] = window.matchMedia(mediaQueries[mq]);

        // Get initial matches for each query.
        theme.mediaMatches[mq] = mqLists[mq].matches;

        // Add an event listener to each query.
        try {
            mqLists[mq].addEventListener('change', handleMqChange);
        } catch (err1) {
            // Fallback for legacy browsers (Safari < 14).
            mqLists[mq].addListener(handleMqChange);
        }
    });
})();

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

window.debounce = debounce;
/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

window.setViewportHeight = setViewportHeight;

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
    document.documentElement.style.setProperty(
        '--scrollbar-width',
        `${window.innerWidth - document.documentElement.clientWidth}px`
    );
}

window.setScrollbarWidth = setScrollbarWidth;


/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
    setViewportHeight();
    setScrollbarWidth();
}

window.setDimensionVariables = setDimensionVariables;

/**
 * Checks if a lazy load image has alternate <source> elements and copies the
 * 'data-src' and 'data-srcset' attributes to 'src' and 'srcset' accordingly.
 * @param {Element} img - Image element.
 */
function setImageSources(img) {
    const setImageAttr = (el) => {
        if (el.dataset.src && !el.src) {
            el.src = el.dataset.src;
        }

        if (el.dataset.srcset && !el.srcset) {
            el.srcset = el.dataset.srcset;
        }
    };

    if (img.parentNode.tagName === 'PICTURE') {
        Array.from(img.parentNode.children).forEach((el) => {
            setImageAttr(el);
        });
    } else {
        setImageAttr(img);
    }
}

window.setImageSources = setImageSources;

function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype === false && 'IntersectionObserver' in window) {
        // If native lazyload not supported but IntersectionObserver supported (Safari).
        const io = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    setImageSources(img);
                    observer.unobserve(img);
                }
            });
        }, { rootMargin: '0px 0px 500px 0px' });

        document.querySelectorAll('[loading="lazy"]').forEach((img) => {
            io.observe(img);
        });
    } else {
        // If native lazy load supported or IntersectionObserver not supported (legacy browsers).
        document.querySelectorAll('[loading="lazy"]').forEach((img) => {
            setImageSources(img);
        });
    }
}
window.initLazyImages = initLazyImages;

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (typeof callback === 'function') {
                        callback();
                        observer.unobserve(entry.target);
                    }
                }
            });
        }, { rootMargin: `0px 0px ${threshold}px 0px` });

        io.observe(element);
    } else {
        callback();
    }
}

window.initLazyScript = initLazyScript;

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 */
function pauseAllMedia(el = document) {
    el.querySelectorAll('.js-youtube, .js-vimeo, video').forEach((video) => {
        const component = video.closest('video-component');
        if (component && component.dataset.background === 'true') return;

        if (video.matches('.js-youtube')) {
            video.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
        } else if (video.matches('.js-vimeo')) {
            video.contentWindow.postMessage('{ "method": "pause" }', '*');
        } else {
            video.pause();
        }
    });

    el.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
    });
}

window.pauseAllMedia = pauseAllMedia;

class DeferredMedia extends HTMLElement {
    constructor() {
        super();

        const loadBtn = this.querySelector('.js-load-media');
        if (loadBtn) {
            loadBtn.addEventListener('click', this.loadContent.bind(this));
        } else {
            this.addObserver();
        }
    }

    /**
     * Adds an Intersection Observer to load the content when viewport scroll is near
     */
    addObserver() {
        if ('IntersectionObserver' in window === false) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.loadContent(false, false, 'observer');
                    observer.unobserve(this);
                }
            });
        }, { rootMargin: '0px 0px 1000px 0px' });

        observer.observe(this);
    }

    /**
     * Loads the deferred media.
     * @param {boolean} [focus=true] - Focus the deferred media element after loading.
     * @param {boolean} [pause=true] - Whether to pause all media after loading.
     * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
     */
    loadContent(focus = true, pause = true, loadTrigger = 'click') {
        if (pause) pauseAllMedia();
        if (this.getAttribute('loaded') !== null) return;

        this.loadTrigger = loadTrigger;
        const content = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.appendChild(content);
        this.setAttribute('loaded', '');

        const deferredEl = this.querySelector('video, model-viewer, iframe');
        if (deferredEl && focus) deferredEl.focus();
    }
}
window.DeferredMedia = DeferredMedia;
customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
    constructor() {
        super();
        this.disclosure = this.querySelector('details');
        this.toggle = this.querySelector('summary');
        this.panel = this.toggle.nextElementSibling;
        this.init();
    }

    init() {
        // Check if the content element has a CSS transition.
        if (window.getComputedStyle(this.panel).transitionDuration !== '0s') {
            this.toggle.addEventListener('click', this.handleToggle.bind(this));
            this.disclosure.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
        }
    }

    /**
     * Handles 'click' events on the summary element.
     * @param {object} evt - Event object.
     */
    handleToggle(evt) {
        evt.preventDefault();

        if (!this.disclosure.open) {
            this.open();
        } else {
            this.close();
        }
    }

    /**
     * Handles 'transitionend' events on the details element.
     * @param {object} evt - Event object.
     */
    handleTransitionEnd(evt) {
        if (evt.target !== this.panel) return;

        if (this.disclosure.classList.contains('is-closing')) {
            this.disclosure.classList.remove('is-closing');
            this.disclosure.open = false;
        }

        this.panel.removeAttribute('style');
    }

    /**
     * Adds inline 'height' style to the content element, to trigger open transition.
     */
    addContentHeight() {
        this.panel.style.height = `${this.panel.scrollHeight}px`;
    }

    /**
     * Opens the details element.
     */
    open() {
        // Set content 'height' to zero before opening the details element.
        this.panel.style.height = '0';

        // Open the details element
        this.disclosure.open = true;

        // Set content 'height' to its scroll height, to enable CSS transition.
        this.addContentHeight();
    }

    /**
     * Closes the details element.
     */
    close() {
        // Set content height to its scroll height, to enable transition to zero.
        this.addContentHeight();

        // Add class to enable styling of content or toggle icon before or during close transition.
        this.disclosure.classList.add('is-closing');

        // Set content height to zero to trigger the transition.
        // Slight delay required to allow scroll height to be applied before changing to '0'.
        setTimeout(() => {
            this.panel.style.height = '0';
        });
    }
}

window.DetailsDisclosure = DetailsDisclosure;

customElements.define('details-disclosure', DetailsDisclosure);

const trapFocusHandlers = {};
window.trapFocusHandlers = trapFocusHandlers;

/**
 * Removes focus trap event listeners and optionally focuses an element.
 * @param {Element} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
}

window.removeTrapFocus = removeTrapFocus;

/**
 * Traps focus within a container, e.g. modal or side drawer.
 * @param {Element} container - Container element to trap focus within.
 * @param {Element} [elementToFocus=container] - Initial element to focus when trap is applied.
 */
function trapFocus(container, elementToFocus = container) {
    const focusableEls = Array.from(
        container.querySelectorAll('summary, a[href], area[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, audio[controls], video[controls], [tabindex]:not([tabindex^="-"])')
    );

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (evt) => {
        if (evt.target !== container && evt.target !== lastEl && evt.target !== firstEl) return;
        document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
        document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (evt) => {
        if (evt.code !== 'Tab') return;

        // If tab pressed on last focusable element, focus the first element.
        if (evt.target === lastEl && !evt.shiftKey) {
            evt.preventDefault();
            firstEl.focus();
        }

        //  If shift + tab pressed on the first focusable element, focus the last element.
        if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
            evt.preventDefault();
            lastEl.focus();
        }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    (elementToFocus || container).focus();
}

window.trapFocus = trapFocus;

class Modal extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Handles 'click' events on the modal.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
        if (evt.target !== this && !evt.target.matches('.js-close-modal')) return;
        this.close();
    }

    /**
     * Opens the modal.
     * @param {Element} opener - Modal opener element.
     */
    open(opener) {
        // Prevent page behind from scrolling when side drawer is open
        this.scrollY = window.scrollY;
        document.body.classList.add('fixed');
        document.body.style.top = `-${this.scrollY}px`;

        this.setAttribute('open', '');
        this.openedBy = opener;

        trapFocus(this);
        window.pauseAllMedia();

        // Add event handler (so the bound event listener can be removed).
        this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

        // Add event listener (for while modal is open).
        this.addEventListener('keyup', this.keyupHandler);
    }

    /**
     * Closes the modal.
     */
    close() {
        // Restore page position and scroll behaviour.
        document.body.style.top = '';
        document.body.classList.remove('fixed');
        window.scrollTo(0, this.scrollY);

        this.removeAttribute('open');

        removeTrapFocus(this.openedBy);
        window.pauseAllMedia();

        // Remove event listener added on modal opening.
        this.removeEventListener('keyup', this.keyupHandler);
    }
}

window.Modal = Modal;
customElements.define('modal-dialog', Modal);

class ModalOpener extends HTMLElement {
    constructor() {
        super();

        const button = this.querySelector('button');
        if (!button) return;

        button.addEventListener('click', () => {
            const modal = document.getElementById(this.dataset.modal);
            if (modal) modal.open(button);
        });
    }
}

window.ModalOpener = ModalOpener;
customElements.define('modal-opener', ModalOpener);

class ProductCard extends HTMLElement {
    constructor() {
        super();
        window.initLazyScript(this, this.init.bind(this));
    }

    init() {
        this.images = this.querySelectorAll('.card__main-image');
        this.links = this.querySelectorAll('.js-prod-link');
        this.quickAddBtn = this.querySelector('.js-quick-add');

        if (this.quickAddBtn) {
            this.productUrl = this.quickAddBtn.dataset.productUrl;
        } else if (this.links.length) {
            this.productUrl = this.links[0].href;
        }

        this.addEventListener('change', this.handleSwatchChange.bind(this));
    }

    /**
     * Handles 'change' events in the product card swatches.
     * @param {object} evt - Event object.
     */
    handleSwatchChange(evt) {
        if (!evt.target.matches('.opt-btn')) return;

        // Swap current card image to selected variant image.
        if (evt.target.dataset.mediaId) {
            const variantMedia = this.querySelector(`[data-media-id="${evt.target.dataset.mediaId}"]`);

            if (variantMedia) {
                this.images.forEach((image) => { image.hidden = true; });
                variantMedia.hidden = false;
            }
        }

        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        const url = `${this.productUrl + separator}variant=${evt.target.dataset.variantId}`;

        // Update link hrefs to url of selected variant.
        this.links.forEach((link) => {
            link.href = url;
        });

        // Update the Quick Add button data.
        if (this.quickAddBtn) {
            this.quickAddBtn.dataset.selectedColor = evt.target.value;
        }
    }
}

window.ProductCard = ProductCard;
customElements.define('product-card', ProductCard);

class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('.qty-input__input');
        this.currentQty = this.input.value;
        this.changeEvent = new Event('change', { bubbles: true });

        this.addEventListener('click', this.handleClick.bind(this));
        this.input.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Handles 'click' events on the quantity input element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
        if (!evt.target.matches('.qty-input__btn')) return;
        evt.preventDefault();

        if (evt.target.name === 'plus') {
            this.input.stepUp();
        } else {
            this.input.stepDown();
        }

        if (this.input.value !== this.currentQty) {
            this.input.dispatchEvent(this.changeEvent);
            this.currentQty = this.input.value;
        }
    }

    /**
     * Handles 'keydown' events on the input field.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
        if (evt.key !== 'Enter') return;
        evt.preventDefault();

        if (this.input.value !== this.currentQty) {
            this.input.blur();
            this.input.focus();
            this.currentQty = this.input.value;
        }
    }
}

window.QuantityInput = QuantityInput;
customElements.define('quantity-input', QuantityInput);

class SideDrawer extends HTMLElement {
    constructor() {
        super();
        this.overlay = document.querySelector('.js-overlay');
    }

    /**
     * Handles a 'click' event on the drawer.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
        if (evt.target.matches('.js-close-drawer') || evt.target === this.overlay) {
            this.close();
        }
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     * @param {Function} [callback] - Callback function to trigger after the open has completed
     */
    open(opener, elementToFocus, callback) {
        console.log("open drawer");
        this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-open`, {
            bubbles: true
        }));

        // Prevent page behind from scrolling when side drawer is open.
        this.scrollY = window.scrollY;
        document.body.classList.add('fixed');
        document.body.style.top = `-${this.scrollY}px`;
        document.documentElement.style.height = '100vh';

        this.overlay.classList.add('is-visible');
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.opener = opener;

        trapFocus(this, elementToFocus);

        // Create event handler variables (so the bound event listeners can be removed).
        this.clickHandler = this.clickHandler || this.handleClick.bind(this);
        this.keyupHandler = (evt) => {
            if (evt.key !== 'Escape' || evt.target.closest('.cart-drawer-popup')) return;
            this.close();
        };

        // Add event listeners (for while drawer is open).
        this.addEventListener('click', this.clickHandler);
        this.addEventListener('keyup', this.keyupHandler);
        this.overlay.addEventListener('click', this.clickHandler);

        // Handle events after the drawer opens
        const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
        setTimeout(() => {
            if (callback) callback();
            this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-open`, {
                bubbles: true
            }));
        }, transitionDuration);
    }

    /**
     * Closes the drawer.
     * @param {Function} [callback] - Call back function to trigger after the close has completed
     */
    close(callback) {
        this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-close`, {
            bubbles: true
        }));

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        this.overlay.classList.remove('is-visible');

        removeTrapFocus(this.opener);

        // Restore page position and scroll behaviour.
        document.documentElement.style.height = '';
        document.body.style.top = '';
        document.body.classList.remove('fixed');
        window.scrollTo(0, this.scrollY);

        // Remove event listeners added on drawer opening.
        this.removeEventListener('click', this.clickHandler);
        this.removeEventListener('keyup', this.keyupHandler);
        this.overlay.removeEventListener('click', this.clickHandler);

        // Handle events after the drawer closes
        const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
        setTimeout(() => {
            if (callback) callback();
            this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-close`, {
                bubbles: true
            }));
        }, transitionDuration);
    }
}

window.SideDrawer = SideDrawer;
customElements.define('side-drawer', SideDrawer);


if (document.readyState === "complete") {
    onPageLoad();
} else {
    window.addEventListener("load", onPageLoad);
}

function onPageLoad() {
    initLazyImages();

    if (window.PLUG.pageType === "index" || window.PLUG.pageType === "product" || window.PLUG.pageType === "collection") {
        if (window.YETT_BLACKLIST && window.yett && typeof window.yett.unblock === "function") {
            let timer2;

            const timer1 = setTimeout(() => {
                if (timer2) {
                    clearTimeout(timer2);
                }
                window.yett.unblock(...PLUG.speed.regExpsToUnblock);
            }, 6000);

            document.addEventListener("mousemove", (evt) => {
                timer2 = setTimeout(() => {
                    if (timer1) {
                        clearTimeout(timer1);
                    }
                    window.yett.unblock(...PLUG.speed.regExpsToUnblock);
                }, 3000);
            }, {passive: true, once: true});
        }
    }

    if (PLUG.pageType !== "index") {
        // document.body.classList.add('dom-loaded');

        // document.addEventListener('keydown', (evt) => {
        //     if (evt.code === 'Tab') {
        //         document.body.classList.add('tab-used');
        //     }
        // }, {passive: true, once: true});

        // Ensure anchor scrolling is smooth (this shouldn't be added in the CSS)
        document.addEventListener('click', (evt) => {
            if (evt.target.tagName === 'A' && window.location.hostname === new URL(evt.target.href).hostname
                && evt.target.href.includes('#')) {
                document.getElementsByTagName('html')[0].style.scrollBehavior = 'smooth';
                setTimeout(() => {
                    document.getElementsByTagName('html')[0].style.scrollBehavior = '';
                }, 1000);
            }
        });
    }

    window.addEventListener(
        'resize',
        debounce(() => {
            setDimensionVariables();
            window.dispatchEvent(new CustomEvent('on:debounced-resize'));
        })
    );

    const announcementBar = document.querySelector('announcement-bar');
    if (announcementBar && typeof announcementBar.resumeSlider === 'function') {
        announcementBar.resumeSlider();
    }
}

/**
 * Provides convenient utility functions for interacting with elements
 */
(() => {
    theme.elementUtil = {};

    /**
     * Allows for removal of elements in one line of code
     * @param {object} elem - Element to remove
     */
    theme.elementUtil.remove = (elem) => {
        if (elem) {
            if (typeof elem.remove === 'function') {
                elem.remove();
            } else {
                elem.forEach((thisElem) => {
                    thisElem.remove();
                });
            }
        }
    };

    /**
     * Checks if the passed element is in viewport or not
     * @param {object} elem - Element to check the view of
     * @returns {boolean}
     */
    theme.elementUtil.isInViewport = (elem) => {
        const rect = elem.getBoundingClientRect();
        return (
            Math.round(rect.top) >= 0
            && Math.round(rect.left) >= 0
            && Math.round(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight)
            && Math.round(rect.right) <= (window.innerWidth || document.documentElement.clientWidth)
        );
    };
})();

/**
 * Utility functions for interacting with LocalStorage/SessionStorage
 */
(() => {
    theme.storageUtil = {};

    theme.storageUtil.set = (key, value, isSession) => {
        if (isSession) {
            sessionStorage.setItem(
                `cc-${key}`,
                typeof value === 'object' ? JSON.stringify(value) : value
            );
        } else {
            localStorage.setItem(`cc-${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
        }
    };

    theme.storageUtil.get = (key, isJson, isSession) => {
        let value = isSession ? sessionStorage.getItem(`cc-${key}`) : localStorage.getItem(`cc-${key}`);
        if (isJson) {
            value = JSON.parse(value);
        }
        return value;
    };

    theme.storageUtil.remove = (key, isSession) => {
        if (isSession) {
            sessionStorage.removeItem(key);
        } else {
            localStorage.removeItem(key);
        }
    };
})();

class CarouselSlider extends HTMLElement {
    constructor() {
        super();
        this.slides = this.querySelectorAll('.slider__item');
        if (this.slides.length < 2) return;
        window.initLazyScript(this, this.init.bind(this));
    }

    disconnectedCallback() {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }

    init() {
        this.slider = this.querySelector('.slider');
        this.grid = this.querySelector('.slider__grid');
        this.nav = this.querySelector('.slider-nav');
        this.rtl = document.dir === 'rtl';
        this.breakpointChangeHandler = this.breakpointChangeHandler
            || this.handleBreakpointChange.bind(this);

        if (this.nav) {
            this.prevBtn = this.querySelector('button[name="prev"]');
            this.nextBtn = this.querySelector('button[name="next"]');
        }

        this.initSlider();
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }

    initSlider() {
        if (!(this.getAttribute('disable-mobile') && !window.matchMedia(theme.mediaQueries.sm).matches)
            && !(this.getAttribute('disable-desktop') && window.matchMedia(theme.mediaQueries.sm).matches)) {
            this.gridWidth = this.grid.clientWidth;

            // Distance between leading edges of adjacent slides (i.e. width of card + gap).
            this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

            // Width of gap between slides.
            this.slideGap = this.slideSpan - this.slides[0].clientWidth;

            this.slidesPerPage = Math.round((this.gridWidth + this.slideGap) / this.slideSpan);
            this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
            this.totalPages = this.slides.length - this.slidesPerPage + 1;

            this.setCarouselState(this.totalPages > 1);
            if (this.totalPages < 2) return;

            this.sliderStart = this.getWindowOffset(this.slider);
            if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
            this.sliderEnd = this.sliderStart + this.gridWidth;

            if (window.matchMedia('(pointer: fine)').matches) {
                this.slider.classList.add('is-grabbable');
            }

            this.addListeners();
            this.setButtonStates();
        } else {
            this.setAttribute('inactive', '');
        }
    }

    addListeners() {
        if (this.nav) {
            this.scrollHandler = debounce(this.handleScroll.bind(this));
            this.navClickHandler = this.handleNavClick.bind(this);

            this.slider.addEventListener('scroll', this.scrollHandler);
            this.nav.addEventListener('click', this.navClickHandler);
        }

        if (window.matchMedia('(pointer: fine)').matches) {
            this.mousedownHandler = this.handleMousedown.bind(this);
            this.mouseupHandler = this.handleMouseup.bind(this);
            this.mousemoveHandler = this.handleMousemove.bind(this);

            this.slider.addEventListener('mousedown', this.mousedownHandler);
            this.slider.addEventListener('mouseup', this.mouseupHandler);
            this.slider.addEventListener('mouseleave', this.mouseupHandler);
            this.slider.addEventListener('mousemove', this.mousemoveHandler);
        }
    }

    removeListeners() {
        if (this.nav) {
            this.slider.removeEventListener('scroll', this.scrollHandler);
            this.nav.removeEventListener('click', this.navClickHandler);
        }

        this.slider.removeEventListener('mousedown', this.mousedownHandler);
        this.slider.removeEventListener('mouseup', this.mouseupHandler);
        this.slider.removeEventListener('mouseleave', this.mouseupHandler);
        this.slider.removeEventListener('mousemove', this.mousemoveHandler);
    }

    /**
     * Handles 'scroll' events on the slider element.
     */
    handleScroll() {
        this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan);
        this.setButtonStates();
    }

    /**
     * Handles 'mousedown' events on the slider element.
     * @param {object} evt - Event object.
     */
    handleMousedown(evt) {
        this.mousedown = true;
        this.startX = evt.pageX - this.sliderStart;
        this.scrollPos = this.slider.scrollLeft;
        this.slider.classList.add('is-grabbing');
    }

    /**
     * Handles 'mouseup' events on the slider element.
     */
    handleMouseup() {
        this.mousedown = false;
        this.slider.classList.remove('is-grabbing');
    }

    /**
     * Handles 'mousemove' events on the slider element.
     * @param {object} evt - Event object.
     */
    handleMousemove(evt) {
        if (!this.mousedown) return;
        evt.preventDefault();

        const x = evt.pageX - this.sliderStart;
        this.slider.scrollLeft = this.scrollPos - (x - this.startX) * 2;
    }

    /**
     * Handles 'click' events on the nav buttons container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
        if (!evt.target.matches('.slider-nav__btn')) return;

        if ((evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl)) {
            this.scrollPos = this.slider.scrollLeft + this.slidesToScroll * this.slideSpan;
        } else {
            this.scrollPos = this.slider.scrollLeft - this.slidesToScroll * this.slideSpan;
        }

        this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
    }

    /**
     * Handles 'on:breakpoint-change' events on the window.
     */
    handleBreakpointChange() {
        this.removeListeners();
        this.initSlider();
    }

    /**
     * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
     * @param {number} el - Element.
     * @returns {number}
     */
    getWindowOffset(el) {
        return this.rtl
            ? window.innerWidth - el.getBoundingClientRect().right
            : el.getBoundingClientRect().left;
    }

    /**
     * Gets the visible state of a slide.
     * @param {Element} el - Slide element.
     * @returns {boolean}
     */
    getSlideVisibility(el) {
        const slideStart = this.getWindowOffset(el);
        const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
        return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
    }

    /**
     * Sets the active state of the carousel.
     * @param {boolean} active - Set carousel as active.
     */
    setCarouselState(active) {
        if (active) {
            this.removeAttribute('inactive');

            // If slider width changed when activated, reinitialise it.
            if (this.gridWidth !== this.grid.clientWidth) {
                this.handleBreakpointChange();
            }
        } else {
            this.setAttribute('inactive', '');
        }
    }

    /**
     * Sets the disabled state of the nav buttons.
     */
    setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) {
            return;
        }

        this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
        this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]);
    }
}

window.CarouselSlider = CarouselSlider;
customElements.define('carousel-slider', CarouselSlider);

class ProductRecommendations extends HTMLElement {
    constructor() {
        super();
        window.initLazyScript(this, this.init.bind(this), 500);
    }

    async init() {
        const { productId } = this.dataset;
        if (!productId) return;

        try {
            const response = await fetch(`${this.dataset.url}&product_id=${productId}`);
            if (!response.ok) throw new Error(response.status);

            const tmpl = document.createElement('template');
            tmpl.innerHTML = await response.text();

            const el = tmpl.content.querySelector('product-recommendations');
            if (el && el.hasChildNodes()) {
                this.innerHTML = el.innerHTML;
            }

            window.initLazyImages();
        } catch (error) {
            console.log(error); // eslint-disable-line
        }
    }
}

window.ProductRecommendations = ProductRecommendations;
customElements.define('product-recommendations', ProductRecommendations);

